import { buildAttachmentPath, sanitizeFileName } from "../attachments.utils";
import type { Attachment, InsertAttachmentReturn } from "@shared/types/supabase.types";
import type { ParsedAttachmentContent, UploadAttachmentResult } from "../attachments.types";
import { supabase } from "@/lib/supabase";

const ATTACHMENTS_BUCKET = "attachments";
const DEFAULT_SIGNED_URL_TTL_SECONDS = 60 * 60;

export async function uploadAttachment({
  containerId,
  messageId,
  file,
}: {
  containerId: string;
  messageId: string;
  file: File;
}): Promise<UploadAttachmentResult> {
  const storagePath = buildAttachmentPath({
    containerId,
    messageId,
    fileName: file.name,
  });

  const { error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .upload(storagePath, file as unknown as Blob, {
      contentType: file.type || undefined,
      upsert: false,
    });

  if (error) {
    throw new Error(`Failed to upload attachment: ${error.message}`);
  }

  return {
    storagePath,
    fileName: sanitizeFileName(file.name),
    mimeType: file.type,
    sizeBytes: file.size,
  };
}

export async function insertAttachmentRow({
  messageId,
  containerId,
  storagePath,
  fileName,
  mimeType,
  sizeBytes,
  createdBy,
  fileSummary,
}: {
  messageId: string;
  containerId: string;
  storagePath: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  createdBy: string;
  fileSummary?: string;
}): Promise<InsertAttachmentReturn> {
  const { data, error } = await supabase.rpc("insert_attachment", {
    p_message_id: messageId,
    p_container_id: containerId,
    p_storage_path: storagePath,
    p_file_name: fileName,
    p_mime_type: mimeType,
    p_size_bytes: sizeBytes,
    p_created_by: createdBy,
    p_file_summary: fileSummary,
  });

  if (error) {
    throw new Error(`Failed to insert attachment row: ${error.message}`);
  }

  return data;
}

export async function updateAttachmentRow({
  id,
  fileSummary,
  parsedContent,
}: {
  id: string;
  fileSummary?: string;
  parsedContent?: ParsedAttachmentContent;
}): Promise<InsertAttachmentReturn> {
  const { data, error } = await supabase.rpc("update_attachment_by_id", {
    p_id: id,
    p_file_summary: fileSummary,
    p_parsed_content: parsedContent,
  });

  if (error) {
    throw new Error(`Failed to update attachment: ${error.message}`);
  }

  return data;
}

export async function getSignedAttachmentUrl({
  storagePath,
  expiresInSeconds = DEFAULT_SIGNED_URL_TTL_SECONDS,
}: {
  storagePath: string;
  expiresInSeconds?: number;
}): Promise<string> {
  const { data, error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, expiresInSeconds);

  if (error || !data.signedUrl) {
    throw new Error(`Failed to create signed URL: ${error?.message ?? "Unknown error"}`);
  }

  return data.signedUrl;
}

export async function listAttachmentsByMessages({
  messageIds,
}: {
  messageIds: string[];
}): Promise<Attachment[]> {
  if (!messageIds.length) {
    return [];
  }

  const { data, error } = await supabase
    .from("attachments")
    .select("*")
    .in("message_id", messageIds)
    .order("created_at", { ascending: true });

  if (error) {
    throw new Error(`Failed to list attachments: ${error.message}`);
  }

  return data;
}
